/**
 * Upgrade To Create Keys Modal
 *
 * Modal shown to Free plan users explaining more API keys require an upgrade.
 *
 * @module components/keys/UpgradeToCreateKeysModal
 */

import { useNavigate } from 'react-router-dom';
import { Modal, Button } from '@/components/ui';
import { Key, Zap, BarChart3, Headphones, ArrowRight } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface UpgradeToCreateKeysModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function UpgradeToCreateKeysModal({ open, onClose }: UpgradeToCreateKeysModalProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/settings/billing');
  };

  const benefits = [
    {
      icon: <Key className="h-5 w-5" />,
      title: 'Up to 5 API keys',
      description: 'Create separate keys for different environments.',
    },
    {
      icon: <Zap className="h-5 w-5" />,
      title: 'Higher rate limits',
      description: 'Up to 50 requests per second.',
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: 'More requests',
      description: 'Up to 50,000 requests per month.',
    },
    {
      icon: <Headphones className="h-5 w-5" />,
      title: 'Email support',
      description: '48-hour response time guarantee.',
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upgrade to Create More Keys"
      description="You've reached the API key limit on the Free plan."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} rightIcon={<ArrowRight className="h-4 w-4" />}>
            View Plans
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-3 pt-2">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="flex items-center gap-3">
              <div className="flex-shrink-0 p-2 bg-brand-50 text-brand-600 rounded-lg">
                {benefit.icon}
              </div>
              <div>
                <p className="font-medium text-gray-900">{benefit.title}</p>
                <p className="text-sm text-gray-500">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
